import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import PostApi from 'mewe/api/post-api';
import PostUtils from 'mewe/utils/post-utils';
import { handleOtherStores } from 'mewe/fetchers/fetch-feed';
import { toPost } from 'mewe/stores/models/post-model';
import FunctionalUtils from 'mewe/shared/functional-utils';
import { installDropdown } from 'mewe/utils/dropdown-utils';

export default class MwSimplePost extends Component {
  @service dynamicDialogs;
  @service account;
  @service analytics;

  @tracked dropdownOptions;
  @tracked displayType;

  constructor() {
    super(...arguments);
    this.displayType = PostUtils.getDisplayType(this.post);

    installDropdown(this, 'dropdownOptions');
  }

  get bookmark() {
    return this.args.bookmark;
  }

  get post() {
    return this.args.post;
  }

  get isPostUnavailable() {
    return !this.post;
  }

  get communityName() {
    if (this.post.group && this.post.group.id !== 'contacts') {
      return this.post.group.name;
    } else if (this.post.event2) {
      return this.post.event2.name;
    } else if (this.post.page) {
      return this.post.page.name;
    }
  }

  get showSecondaryAvatar() {
    // don't reveal the page admin/owner who created page post
    return this.post.group && this.post.group.id !== 'contacts' || this.post.event2;
  }

  get otherTypeText() {
    switch (this.displayType) {
      case 'poll':
        return __('Poll: {question}', { question: this.post.poll.question });
      case 'audio':
        return __('Audio file', { filename: this.post.files[0].name });
      case 'file':
        return this.post.files[0].name
          ? __('Document: {filename}', { filename: this.post.files[0].name })
          : __('Document');
      case 'link':
        return __('Link');
    }
  }

  get queryScope() {
    if (this.post.groupId) {
      return `/group/${this.post.groupId}/scheduled/post`;
    } else if (this.args.page?.id) {
      return `/pages/page/${this.args.page.id}/scheduled/post`;
    } else {
      return `/home/scheduled/post`;
    }
  }

  extractPostData(model) {
    let post_has_photos = !!model.photo && !model.isVideo;
    let post_type;

    if (model.eventId) {
      post_type = 'event';
    } else if (model.groupId) {
      post_type = 'group';
    } else if (model.pageId) {
      post_type = 'page';
    } else if (model.threadId) {
      post_type = 'private';
    } else if (model.closeFriends) {
      post_type = 'favorites';
    } else if (model.everyone) {
      post_type = 'everyone_on_mewe';
    } else if (model.public) {
      post_type = 'anyone';
    } else {
      post_type = 'followers';
    }

    let postData = {
      post_id: model.id,
      reshare_post_id: model.refPost?.id,
      post_image_is_nsfw: post_has_photos
        ? model?.medias?.find((media) => media?.photo?.isNsfw)
          ? true
          : false
        : null,
      post_has_ai_stickers: model.aiGeneratedStickers?.length > 0,
      post_comments_count: model.comments?.total,
      post_reshare_count: model.sharesCount,
      post_has_files: model.files?.length > 0,
      post_has_gifs: model.gifUrls?.length > 0,
      post_has_photos,
      post_has_stickers: model.stickers?.length > 0,
      post_has_polls: !!model.poll,
      post_has_videos: model.isVideo,
      post_type,
    };

    // Remove properties with undefined values
    Object.keys(postData).forEach(
      (key) => (postData[key] === undefined || postData[key] === null) && delete postData[key]
    );
    return postData;
  }

  @action
  removeBookmark() {
    this.args.removeBookmark(this.bookmark);
  }

  @action
  openPost() {
    let { scope, scopeId } = PostUtils.getPostScopeAndId(this.post);

    PostApi.getPostDetails({
      scope: scope,
      groupId: scopeId,
      pageId: scopeId,
      eventId: scopeId,
      threadId: scopeId,
      postItemId: this.post.postItemId,
    })
      .then((data) => {
        handleOtherStores(data);

        let postE = toPost(data.post);
        let eventData = this.extractPostData(postE);
        this.analytics.sendEvent('bookmarkedPostOpened', eventData);

        this.dynamicDialogs.openDialog('single-post-dialog', {
          post: postE,
          scope: scope,
          onRemovePost: () => this.args.onRemovePost(thePost)
        });
      })
      .catch((resp) => {
        if (resp && resp.status === 404) {
          FunctionalUtils.error(__('Sorry, this post has been deleted by the owner.'));
        }
      });
  }

  @action
  clickHandler(e) {
    if (this.args.variant === 'search') {
      this.openPost();
      return;
    }

    if (this.args.variant === 'bookmark') {
      if (e.target.closest('.post_header-info')) return;
      if (e.target.closest('.c-mw-postbox-schedule')) return;
      if (e.target.closest('button')) return; // close button for unavailable post
      else this.openPost();
    }

    if (this.args.variant === 'scheduled') {
      if (e.target.closest('.post_header-info')) return; // close button for unavailable post
      if (this.post.isEditingSchedule) return; // ignore click actions when user edits schedule
      this.openScheduledPost();
    }
  }

  @action
  editPost() {
    set(this.post, 'isEditing', true);
    this.openScheduledPost();
  }

  openScheduledPost() {
    if (this.args.variant === 'scheduled') {
      this.dynamicDialogs.openDialog('single-post-dialog', {
        post: this.post,
      });
    }
  }

  @action
  hideSchedule() {
    set(this, 'post.isEditingSchedule', false);
  }

  @action
  schedule(timestamp) {
    const post = this.post;

    if (this.isSharing) return;

    PostApi.reschedule({
      params: {
        schedule: timestamp,
      },
      postId: post.id,
      scope: this.queryScope,
    })
      .then((data) => {
        post.setProperties({
          isEditingSchedule: false,
        });

        this.updateDate(timestamp);

        FunctionalUtils.info(__('Post Rescheduled!'));
      })
      .catch((error) => {
        if (error.status === 400 && error.data?.message)
          FunctionalUtils.error(__(`Sorry, this post can't be rescheduled. An error occured`));
      });
  }

  @action
  updateDate(newTimestamp) {
    set(this, 'post.scheduled', newTimestamp);
  }

  @action
  publishPost() {
    this.args.publishPost?.([this.post]);
  }

  @action
  deletePost() {
    this.args.deletePost?.([this.post]);
  }
}
